import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  white,
  gray100,
  gray40,
  gray80,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../constants/colors';
import { AuthContext } from '../../contexts/auth';
import { useAccess } from '../../contexts';
import { GroupContext } from '../../contexts/group';
import { getAllGroups, deleteGroup } from '../../api/firebase/firestore/group';
import { getAllUsers } from '../../api/firebase/firestore/user';
import SideBar from '../uiParts/SideBar';
import MetaTitle from '../utils/MetaTitle';
import CustomSnackbar from '../uiParts/CustomSnackbar';
import DeleteModal from '../uiParts/DeleteModal';
import CustomLoading from '../uiParts/CustomLoading';
import { userRole } from '../../constants/enum';
import type { GroupType } from '../../validation/group/groupType';
import type { DocumentData } from 'firebase/firestore';

const styles = {
  page: {
    height: '100%',
    display: 'flex',
    paddingBottom: '236px',
    '@media (max-width: 960px)': {
      flexDirection: 'column',
      paddingTop: '0',
      justifyContent: 'flex-start',
      paddingBottom: '100px',
    },
  },
  pageBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    marginLeft: '240px',
    height: '80px',
    textAlign: 'center',
    backgroundColor: white,
    borderBottom: `1px solid ${gray40}`,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 24px',
    '@media (max-width: 960px)': {
      height: '41px',
      marginTop: '48px',
      padding: '0 10px 0 16px',
      borderTop: `1px solid ${gray40}`,
      marginLeft: '0',
    },
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: 700,
    '@media (max-width: 960px)': {
      fontSize: '16px',
    },
  },
  container: {
    marginLeft: '240px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '24px',
    paddingRight: '16px',
    paddingLeft: '16px',
    width: '-webkit-fill-available',
    '@media (max-width: 960px)': {
      height: '100%',
      alignItems: 'flex-start',
      marginLeft: '0',
      paddingRight: '0',
      paddingLeft: '0',
    },
  },
  sidebar: {
    position: 'fixed',
    color: 'white',
    textAlign: 'center',
    backgroundColor: gray100,
    width: '240px',
    height: '100vh',
    flexShrink: 0,
    zIndex: 1000,
    '@media (max-width: 960px)': {
      width: '100%',
      minWidth: '240px',
      height: '48px',
    },
  },
  button: {
    border: 'none',
    fontWeight: 700,
    fontSize: '16px',
    textAlign: 'center',
    width: '192px',
    height: '44px',
    '@media (max-width: 960px)': {
      width: '100%',
    },
  },
  tableHeader: {
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
  },
  tableCell: {
    padding: '12px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
  },
  paper: {
    padding: '20px',
    width: '100%',
    backgroundColor: white,
    border: '1px solid #E0E0E0',
    overflowY: 'auto',
  },
};

const GroupManage: React.FC = () => {
  const [groups, setGroups] = useState<GroupType[]>([]);
  const [usersMap, setUsersMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const { user, isLoading } = useContext(AuthContext);
  const { refreshGroups } = useContext(GroupContext);
  const { allowAccess } = useAccess();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    if (user === null) {
      navigate('/');
      return;
    }
    if (user.role !== userRole.admin) {
      navigate('/ai-ocr');
      return;
    }

    const fetchData = async (): Promise<void> => {
      setLoading(true);
      try {
        const [fetchedGroups, fetchedUsers] = await Promise.all([getAllGroups(), getAllUsers()]);
        setGroups(fetchedGroups);
        const map: Record<string, string> = {};
        fetchedUsers.forEach((u: DocumentData) => {
          map[u.uid] = u.name ?? u.email;
        });
        setUsersMap(map);
      } catch {
        setSnackbarMessage('データの取得に失敗しました。');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, isLoading]);

  const handleDelete = async (): Promise<void> => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      await deleteGroup(selectedGroupId, user.uid);
      setGroups(prev => prev.filter(g => g.groupId !== selectedGroupId));
      await refreshGroups();
      setInfoMessage('グループを削除しました。');
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('グループの削除に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
    }
  };

  return (
    <>
      <MetaTitle />
      <CustomLoading loading={loading} />
      <Box sx={styles.page}>
        <Box sx={styles.sidebar}>
          <SideBar />
        </Box>
        <Box sx={styles.pageBox}>
          <Box sx={styles.header}>
            <Typography sx={styles.headerTitle}>グループ管理</Typography>
            <Button
              onClick={(): void => navigate('/groups/create')}
              variant="outlined"
              sx={[primaryButtonBackgroundColor, styles.button]}
            >
              ＋ グループを作成
            </Button>
          </Box>
          <Box sx={styles.container}>
            <Paper elevation={0} sx={styles.paper}>
              <TableContainer sx={{ minWidth: '600px', overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={styles.tableHeader}>グループ名</TableCell>
                      <TableCell sx={styles.tableHeader}>説明</TableCell>
                      <TableCell sx={styles.tableHeader}>メンバー</TableCell>
                      <TableCell sx={styles.tableHeader} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map(group => (
                      <TableRow key={group.groupId}>
                        <TableCell sx={styles.tableCell}>{group.name}</TableCell>
                        <TableCell sx={styles.tableCell}>{group.description || '-'}</TableCell>
                        <TableCell sx={styles.tableCell}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {group.memberUids.map(uid => (
                              <Chip
                                key={uid}
                                label={usersMap[uid] ?? uid}
                                size="small"
                                variant="outlined"
                                clickable
                                onClick={(): void => {
                                  allowAccess('/staff/edit');
                                  navigate('/staff/edit', {
                                    state: { id: uid, isAdmin: true },
                                  });
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={styles.tableCell}>
                          <Button
                            sx={[secondaryButtonBackgroundColor, { height: '32px' }]}
                            onClick={(): void => navigate(`/groups/${group.groupId}/edit`)}
                          >
                            編集
                          </Button>
                          <Button
                            sx={[
                              tertiaryButtonBackgroundColor,
                              { height: '32px', marginLeft: '16px' },
                            ]}
                            onClick={(): void => {
                              setSelectedGroupId(group.groupId);
                              setDeleteModalOpen(true);
                            }}
                          >
                            削除
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {groups.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={styles.tableCell}>
                          グループがありません
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </Box>
      </Box>
      <DeleteModal
        open={deleteModalOpen}
        onClose={(): void => setDeleteModalOpen(false)}
        onDelete={handleDelete}
        title="グループ"
      />
      <CustomSnackbar
        autoHideDuration={6000}
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={snackbarMessage}
        infoText={infoMessage}
      />
    </>
  );
};

export default GroupManage;
